/**
 * Active Sport persistence (ADR-030 Global Sport Mode).
 * One selected sport filters Home / Arena / Discover / Intelligence / History.
 * Session live scoring still uses SportProvider with Arena `sportId` (ADR-032).
 *
 * Prompt cadence lives in `sportSelectionFrequency.js` — not in UI.
 */

import { DEFAULT_SPORT, normalizeSportId } from "./sportTypes";
import { shouldPromptSportSelection } from "./sportSelectionFrequency";

const STORAGE_KEY = "tra.activeSport";
const CHOSEN_KEY = "tra.activeSportChosen";
const SELECTED_AT_KEY = "tra.activeSportSelectedAt";

/**
 * @deprecated Prefer sportSelectionFrequency.CURRENT_SPORT_SELECTION_FREQUENCY.
 * @type {number | null}
 */
export const ACTIVE_SPORT_MAX_AGE_MS = null;

/**
 * @returns {boolean}
 */
export function hasChosenActiveSport() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  try {
    if (window.localStorage.getItem(CHOSEN_KEY) === "1") return true;
    return window.localStorage.getItem(STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

/**
 * @returns {import("./sportTypes").SportId}
 */
export function getActiveSportId() {
  if (typeof window === "undefined" || !window.localStorage) {
    return DEFAULT_SPORT;
  }
  try {
    return normalizeSportId(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_SPORT;
  }
}

/**
 * @returns {number | null} epoch ms when sport was last chosen, or null
 */
export function getActiveSportSelectedAt() {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SELECTED_AT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Whether to present “Choose Today's Sport”.
 * Delegates to the frequency strategy so UI never embeds cadence rules.
 * @returns {boolean}
 */
export function shouldPromptActiveSportSelection() {
  return shouldPromptSportSelection({
    hasChosen: hasChosenActiveSport(),
    selectedAt: getActiveSportSelectedAt(),
  });
}

/**
 * @param {string | null | undefined} sportId
 * @returns {import("./sportTypes").SportId}
 */
export function setActiveSportId(sportId) {
  const next = normalizeSportId(sportId);
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      window.localStorage.setItem(CHOSEN_KEY, "1");
      window.localStorage.setItem(SELECTED_AT_KEY, String(Date.now()));
    } catch {
      /* ignore quota */
    }
  }
  return next;
}
